import {
  get,
  filter,
  find,
  findIndex,
  uniqBy,
  isEmpty,
  cloneDeep,
  forEach,
  has,
  first,
  isFinite,
  sortBy,
  uniq,
} from 'lodash';

import {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_DESCRIPTION,
  QUESTION_NPS,
  QUESTION_RATING,
  QUESTION_RANKING,
  QUESTION_SLIDER,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_CONSTANT_SUM,
  GROUP_BASIC,
  GROUP_LOOP_MERGE,
  GROUP_AB_TEST,
  GROUP_CONJOINT,
  GROUP_VAN_WESTENDORP,
  GROUP_MAXDIFF,
  QUESTION_DRILLDOWN,
  QUESTION_CONVERSATIONAL,
  QUESTION_MAXDIFF,
  NODE_TYPES_SUPPORT_SIMPLE_QUOTAS,
} from 'data/nodeTypes';

import { QUESTIONS_SUPPORTING_LOGIC_ANSWERS } from 'data/featuresPerQuestion';

import {
  Matcher,
  NodeStatements,
  OutcomeType,
  questionnaireLogicService,
  questionnairePathTraversal,
  conditionService,
  ConceptConditionContext,
  QuotaConditionContext,
  Statement,
  Rule,
  Outcome,
  Operator,
  NextNode,
  QuestionnaireConditionContext,
  VisibilityContext,
  Questionnaire,
  VirtualEntryGroupPostfix,
  FinalNode,
  FinalGroupNode,
  ActionVisibilityValueType,
  QuotaConditionTypeEnum,
} from 'utils/adl-services';
import {
  isMatrixOpenEndedType,
  isMatrixSingleOrMultipleNode,
  isGroup,
  isLoopAndMergeGroup,
  isDrillDownNode,
  isDrillDownQuestionType,
  isMatrixSingleMultipleOrBipolarQuestionType,
  isSingleQuestionType,
  isMultipleQuestionType,
  isMatrixSingleQuestionType,
  isMatrixMultipleQuestionType,
  isNewMatrixNode,
  isConjointGroup,
  isMaxDiffGroup,
} from 'utils/nodeTypes';
import {
  LOGIC_MODES,
  LOGIC_MODE_KEYS,
  DISQUALIFY_PLACEHOLDER_QUESTION,
} from 'containers/QuestionnairePage/constants';
import { END_OF_SURVEY } from 'components/LogicPathPreview/constants';
import { isValueExistOnTree } from 'containers/QuestionnairePage/GenericQuestion/DrillDownQuestion/utils';

import { QUOTA_LOGIC_DEFAULT_VALUE } from 'components/LogicQuota/constants';
import { getDefaultMatcherForQuota } from 'components/LogicMatcher/utils';
import {
  isQuotaFullReason,
  isScreeningStatement,
} from 'components/LogicOutcome/utils';

import {
  REASON_QUESTIONNAIRE_QUOTA_FULL,
  REASON_SCREENING_QUOTA_FULL,
  REASON_SCREENOUT,
} from 'components/LogicOutcome/constants';
import { isSingleAssignmentStrategy } from 'utils/quotaAssignmentStrategy';
import {
  getNodeByRefIdDeep,
  getQuestionSupportsQuotaAssignment,
  getUnconditionallyHiddenItems,
} from '../QuestionnairePage/utils';

import { getConceptBySourceId } from '../QuestionnairePage/NodeSettings/settings/utils/CarryForward';
import { handleMissingQuotasForNode } from '../QuestionnairePage/reducer_ADL_utils';
import { qlRemoveLogicOfItemAndCopies } from '../QuestionnairePage/carriedForwardCopies';
import {
  normalizeScreeningNavigation,
  makeNewlyEligibleScreeningQuotaItems,
  qlOrderScreeningStatements,
  qlSetScreenerTypes,
  qlSetScreeningStartingShape,
  qlUpdateScreeningNode,
  qlUpdateScreeningQuotas,
} from '../QuestionnairePage/screening';

const QUESTION_TYPES_WITH_NO_ANSWER = [
  QUESTION_SLIDER,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
];

export const getLogicPathsForNode = ({
  nodeRefId,
  logicPaths = [],
  questions = [],
  errors = [],
}) => {
  const logicData = {
    comesFrom: [],
    goesTo: [],
    goesToWithErrors: [],
  };

  // errors are only going to be node loops currently
  const questionErrors = filter(errors, ['nodeId', nodeRefId]);
  logicPaths.forEach((logicPath) => {
    const [comesFrom, goesToOriginal, reason] = logicPath;
    const goesTo = getNodeRefIdWithoutPostFix(goesToOriginal);
    const sourceNode = getNodeByRefIdDeep(questions, comesFrom);
    const destinationRefId =
      goesTo === FinalGroupNode ? sourceNode.groupRefId : goesTo;
    const destinationNode = getNodeByRefIdDeep(questions, destinationRefId);
    const goesToEndGroup =
      isGroup(destinationNode) &&
      sourceNode.groupRefId === destinationNode.refId;

    try {
      if (comesFrom === nodeRefId) {
        // end of survey
        if (goesTo === FinalNode) {
          logicData.goesTo.push({
            endOfSurvey: true,
            // there is currently no error that can happen if the destinationNode goes to end of survey
            hasError: false,
            reason,
          });
        } else {
          const hasError = questionErrors.some(
            (error) => error.details.to === goesTo,
          );
          if (hasError) {
            logicData.goesToWithErrors.push({
              refId: goesTo,
              qIndex: destinationNode.questionIndex,
              qType: destinationNode.type,
              qLabel: destinationNode.label,
              hasError,
              goesToEndGroup,
            });
          } else {
            logicData.goesTo.push({
              refId: goesTo,
              qIndex: destinationNode.questionIndex,
              qType: destinationNode.type,
              qLabel: destinationNode.label,
              hasError,
              goesToEndGroup,
            });
          }
        }
      }

      if (goesTo === nodeRefId) {
        logicData.comesFrom.push({
          refId: comesFrom,
          qIndex: sourceNode.questionIndex,
          qType: sourceNode.type,
          qLabel: sourceNode.label,
          goesToEndGroup,
        });
      }
    } catch (e) {
      console.error(e);
    }
  });

  // remove duplicate questions; end-of-survey entries all share an undefined
  // refId, so keep a researcher-authored one over a quota-full one
  return {
    comesFrom: uniqBy(logicData.comesFrom, 'refId'),
    goesTo: uniqBy(
      sortBy(logicData.goesTo, ({ reason }) => isQuotaFullReason(reason)),
      'refId',
    ),
    goesToWithErrors: uniqBy(logicData.goesToWithErrors, 'refId'),
  };
};

export const shouldKeepLogic = (currentType, previousType) =>
  (isSingleQuestionType(currentType) && isMultipleQuestionType(previousType)) ||
  (isMultipleQuestionType(currentType) && isSingleQuestionType(previousType)) ||
  (isMatrixSingleQuestionType(currentType) &&
    isMatrixMultipleQuestionType(previousType)) ||
  (isMatrixMultipleQuestionType(currentType) &&
    isMatrixSingleQuestionType(previousType));

export const getAnswersAdded = (
  previousAnswers = [],
  currentAnswers = [],
  type,
) =>
  currentAnswers.filter(
    (currentAnswer) => !includesById(previousAnswers, currentAnswer, type),
  );

export const getAnswersRemoved = (
  previousAnswers = [],
  currentAnswers = [],
  type,
) =>
  filter(
    previousAnswers,
    (previousAnswer) => !includesById(currentAnswers, previousAnswer, type),
  );

const includesById = (array = [], object = {}, type) => {
  const attr = isDrillDownQuestionType(type) ? 'body' : 'refId';

  return array.some((value) => value[attr] === object[attr]);
};

export const getTaxonomyCondition = ({
  matcher,
  taxonomyProperty,
  defaultValue = '',
}) => {
  if (!matcher) {
    throw new Error('No default matcher provided for getTaxonomyCondition');
  }

  return conditionService.createTaxonomyCondition(
    defaultValue,
    taxonomyProperty,
    matcher,
  );
};

export const getConceptCondition = ({
  matcher,
  nodeRefId,
  defaultValue = '',
}) => {
  if (!matcher) {
    throw new Error('No default matcher provided for getConceptCondition');
  }

  return conditionService.createCondition(
    defaultValue,
    new ConceptConditionContext(nodeRefId),
    matcher,
  );
};

export const getQuotaCondition = ({
  matcher = getDefaultMatcherForQuota(),
  ruleId,
  defaultValue = QUOTA_LOGIC_DEFAULT_VALUE,
}) =>
  conditionService.createCondition(
    defaultValue,
    new QuotaConditionContext(ruleId),
    matcher,
  );

export const getAnswerCondition = ({
  questionRefId,
  matcher,
  defaultValue = '',
  context,
  isText,
}) => {
  let condition;

  if (!matcher) {
    throw new Error('No default matcher provided for getAnswerCondition');
  }

  const referencedQuestion = get(context, 'questionId');

  if (matcher === Matcher.Contains) {
    condition = conditionService.createAnswerConditionContains(
      questionRefId,
      defaultValue,
      undefined,
      referencedQuestion,
    );
  } else if (matcher === Matcher.GreaterThan) {
    condition = conditionService.createAnswerConditionGreaterThan(
      questionRefId,
      defaultValue,
      undefined,
      referencedQuestion,
    );
  } else if (matcher === Matcher.GreaterThanEqual) {
    condition = conditionService.createAnswerConditionGreaterThanEqual(
      questionRefId,
      defaultValue,
      undefined,
      referencedQuestion,
    );
  } else if (matcher === Matcher.LowerThan) {
    condition = conditionService.createAnswerConditionLowerThan(
      questionRefId,
      defaultValue,
      undefined,
      referencedQuestion,
    );
  } else if (matcher === Matcher.LowerThanEqual) {
    condition = conditionService.createAnswerConditionLowerThanEqual(
      questionRefId,
      defaultValue,
      undefined,
      referencedQuestion,
    );
  } else if (matcher === Matcher.Equal) {
    const valueType = isText ? 'text' : undefined;

    condition = conditionService.createAnswerConditionEqual(
      questionRefId,
      defaultValue,
      undefined,
      referencedQuestion,
      valueType,
    );
  } else if (matcher === Matcher.NotEqual) {
    const valueType = isText ? 'text' : undefined;

    condition = conditionService.createAnswerConditionNotEqual(
      questionRefId,
      defaultValue,
      undefined,
      referencedQuestion,
      valueType,
    );
  }
  // TODO RES-4563
  // else if (matcher === Matcher.ContainsWord) {
  //   condition = conditionService.createAnswerConditionContainsWord(
  //     questionRefId,
  //     defaultValue,
  //     undefined,
  //     referencedQuestion,
  //   );
  // }
  else {
    throw new Error('Unhandled matcher provided for getAnswerCondition');
  }

  return condition;
};
export function getVisibilityCondition({
  questionRefId,
  matcher,
  defaultValue = '',
}) {
  if (!matcher) {
    throw new Error('No default matcher provided for getVisibilityCondition');
  }

  return conditionService.createCondition(
    defaultValue,
    new VisibilityContext(questionRefId),
    matcher,
  );
}

const getScreeningNodeIds = (ql) =>
  new Set(
    (ql?.questionnaire?.nodes || [])
      .filter((node) => node.isScreening)
      .map((node) => node.id),
  );

const stripScreeningNavigationStatements = (ql) => {
  const screeningNodeIds = getScreeningNodeIds(ql);

  if (screeningNodeIds.size === 0) {
    return ql;
  }

  return {
    ...ql,
    nodeStatements: (ql.nodeStatements || []).map((nodeStatement) => {
      if (!screeningNodeIds.has(nodeStatement.nodeId)) {
        return nodeStatement;
      }
      const screeningNavigationStatements =
        questionnaireLogicService.getGraphStatementsWithRule(nodeStatement);
      return {
        ...nodeStatement,
        statements: (nodeStatement.statements || []).filter(
          // Reference-equality is safe: getGraphStatementsWithRule filters this same
          // statements array, so the objects are identical references. Would break if
          // it ever returned mapped/cloned statements.
          (statement) => !screeningNavigationStatements.includes(statement),
        ),
      };
    }),
  };
};

// Screening nodes keep a fail-closed default (→ FinalNode) that getNodesWithLogic
// treats as authored non-passthrough logic. After stripping screening navigation,
// rewrite orphan defaults to the questionnaire next node so screening-only nodes
// drop out of nodesWithNonScreeningLogic. Nodes that still have visibility (or
// other non-default) statements keep their fail-closed default so they stay in.
// Uses questionnaireLogicService.findPassthroughStatementByNode /
// updateDefaultStatementOutcomeForNode (wrappers over graph.getDefaultGraphStatement).
const normalizeOrphanScreeningDefaultsToPassthrough = (ql) => {
  const screeningNodeIds = getScreeningNodeIds(ql);

  if (screeningNodeIds.size === 0) {
    return ql;
  }

  return [...screeningNodeIds].reduce((accQl, nodeId) => {
    const nodeStatement =
      questionnaireLogicService.findNodeStatementsByNodeIdWithNoErrorThrown(
        accQl,
        nodeId,
      );
    if (!nodeStatement) {
      return accQl;
    }

    const defaultStatement =
      questionnaireLogicService.findPassthroughStatementByNode(accQl, nodeId);
    if (!defaultStatement) {
      return accQl;
    }

    const hasNonDefaultStatements = (nodeStatement.statements || []).some(
      // Reference-equality is safe: findPassthroughStatementByNode returns the
      // statement object from this same statements array.
      (statement) => statement !== defaultStatement,
    );
    if (hasNonDefaultStatements) {
      return accQl;
    }

    const { nextNodeId } = Questionnaire.getNodeInfo(
      accQl.questionnaire,
      nodeId,
    );
    return questionnaireLogicService.updateDefaultStatementOutcomeForNode(
      accQl,
      nodeId,
      nextNodeId,
    );
  }, ql);
};

// Display path set for QuestionFooter comes-from / goes-to labels: strip screening
// navigation, normalize orphan fail-closed defaults to passthrough, then analyze.
// Full logicPaths stay on the unstripped QL for reachability / "is set" consumers.
export const getLogicPathsWithoutScreeningNavigation = (ql) => {
  const { logicPaths } = questionnairePathTraversal.analyzeGraphPaths(
    normalizeOrphanScreeningDefaultsToPassthrough(
      stripScreeningNavigationStatements(ql),
    ),
  );
  return logicPaths;
};

// Answer-level Show/Hide on a screening node does not always put the node itself
// into getNodesWithLogic (especially once orphan defaults are normalized). Union
// screening nodes whose answers still carry non-screening logic.
const getScreeningNodesWithNonScreeningAnswerLogic = (
  ql,
  answersWithNonScreeningLogic,
) => {
  const answersSet = new Set(answersWithNonScreeningLogic);
  if (answersSet.size === 0) {
    return [];
  }

  return (ql?.questionnaire?.nodes || [])
    .filter((node) => node.isScreening)
    .filter((node) =>
      (node.answers || []).some((answer) => answersSet.has(answer.id)),
    )
    .map((node) => node.id);
};

export const updateState = ({
  ql,
  questionnaireLogic,
  shouldUpdateVersion = true,
}) => {
  const {
    all: nodesWithLogic,
    fromOutcomesShow: nodesWithLogicFromOutcomesShow,
    fromOutcomesHide: hiddenNodes,
    nodesWithSimpleQuotaOnlyLogic,
    nodesWithAdvancedQuotaOnlyLogic,
  } = questionnaireLogicService.getNodesWithLogic(ql);

  const {
    all: answersWithLogic,
    fromOutcomesShow: answersWithLogicFromOutcomesShow,
    fromOutcomesHide: answersWithLogicFromOutcomesHide,
  } = questionnaireLogicService.getAnswersWithLogic(ql);
  const answersWithLogicFromOutcomes = sortBy([
    ...answersWithLogicFromOutcomesShow,
    ...answersWithLogicFromOutcomesHide,
  ]);

  // Recompute answers/nodes-with-logic and display paths against a copy that has
  // screening-navigation statements stripped. The flat de-duplicated sets above
  // have no per-item provenance, so they can't be filtered down here; the second
  // pass is the simplest correct way to exclude items whose only logic is their
  // own screening navigation. Nodes also need orphan fail-closed defaults
  // normalized (and answer-visibility targets re-unioned) — see helpers above.
  const qlWithoutScreeningNavigation = stripScreeningNavigationStatements(ql);
  const qlNormalizedWithoutScreeningNavigation =
    normalizeOrphanScreeningDefaultsToPassthrough(qlWithoutScreeningNavigation);
  const { all: answersWithNonScreeningLogic } =
    questionnaireLogicService.getAnswersWithLogic(qlWithoutScreeningNavigation);
  const { all: nodesFromNonScreeningLogic } =
    questionnaireLogicService.getNodesWithLogic(
      qlNormalizedWithoutScreeningNavigation,
    );
  const nodesWithNonScreeningLogic = uniq([
    ...nodesFromNonScreeningLogic,
    ...getScreeningNodesWithNonScreeningAnswerLogic(
      ql,
      answersWithNonScreeningLogic,
    ),
  ]);

  const conceptsWithLogic = questionnaireLogicService.getConceptsWithLogic(ql);
  const taxonomyWithLogic = questionnaireLogicService.getTaxonomyWithLogic(ql);
  const variablesWithLogic =
    questionnaireLogicService.getVariablesWithLogic(ql);

  const basicLogicMap = [];
  const {
    errors,
    logicPaths,
    previousNodesPerNode,
    adjacencyList,
    maxPathLength,
  } = questionnairePathTraversal.analyzeGraphPaths(ql);
  const { logicPaths: logicPathsWithoutScreeningNavigation } =
    questionnairePathTraversal.analyzeGraphPaths(
      qlNormalizedWithoutScreeningNavigation,
    );

  const newState = {
    ql,
    maxQlPath: maxPathLength,
    basicLogicMap,
    previousNodesPerNode,
    adjacencyList,
    logicPaths,
    logicPathsWithoutScreeningNavigation,
    nodesWithLogic,
    nodesWithNonScreeningLogic,
    nodesWithLogicFromOutcomesShow,
    nodesWithSimpleQuotaOnlyLogic,
    nodesWithAdvancedQuotaOnlyLogic,
    answersWithLogic,
    answersWithNonScreeningLogic,
    answersWithLogicFromOutcomesShow,
    answersWithLogicFromOutcomesHide,
    answersWithLogicFromOutcomes,
    conceptsWithLogic,
    taxonomyWithLogic,
    variablesWithLogic,
    hiddenNodes,
    version: questionnaireLogic.version,
    rulesWithQuotas: questionnaireLogicService.getRulesWithQuota(ql),
  };

  if (errors) {
    newState.errors = errors;
  }

  if (shouldUpdateVersion) {
    newState.version = questionnaireLogic.version + 1;
  }

  return newState;
};

const transformNodesForUseWithQuestionnaireLogic = (nodes) =>
  nodes.map((node) =>
    transformNodeToQlQuestionnaireNode({
      node,
      sameLevelNodes: nodes,
      withNextNode: true,
      defaultNextNode: FinalNode,
    }),
  );

export const getNonScreeningQuestionsOrdered = (questions) =>
  questions
    .filter((q) => !q.isScreening)
    .map((q) => ({
      refId: q.refId,
      nodes: q.questions
        ? getNonScreeningQuestionsOrdered(q.questions)
        : undefined,
    }));

export const initQlAfterFetch = ({
  ql,
  questionnaireLogic,
  nodes,
  surveyId,
  logicMode = LOGIC_MODES.PLAIN,
}) => {
  if (!isEmpty(ql)) {
    try {
      const migratedQl = questionnaireLogicService.migrate(ql);
      const newQl =
        logicMode === LOGIC_MODES.PLAIN
          ? normalizeScreeningNavigation(migratedQl)
          : migratedQl;

      return updateState({
        ql: newQl,
        questionnaireLogic,
        shouldUpdateVersion: false,
      });
    } catch (error) {
      return {
        ...questionnaireLogic,
        error,
      };
    }
  }

  try {
    const defaultQl = questionnaireLogicService.buildDefaultQuestionnaireLogic(
      {
        surveyId,
        nodes:
          logicMode === LOGIC_MODES.DISQUALIFICATION
            ? [DISQUALIFY_PLACEHOLDER_QUESTION]
            : transformNodesForUseWithQuestionnaireLogic(nodes),
      },
      {
        defaultMatcher: Matcher.Equal,
        expandConditions: false,
      },
    );
    return updateState({
      ql: defaultQl,
      questionnaireLogic,
    });
  } catch (error) {
    return {
      ...questionnaireLogic,
      error,
    };
  }
};

// `screenerTypes` (`{ [answerRefId]: screenerType }`) sets the screener types
// of a screening node added from a copy (duplicated or imported).
export const updateQlAfterNodeAdd = ({
  questionnaireLogic,
  node,
  refId,
  offset = 1,
  screenerTypes = {},
}) => {
  const nodes = get(questionnaireLogic, 'ql.questionnaire.nodes', []);
  let siblingNodes;
  const { groupRefId } = node;

  let parentGroupType;
  if (groupRefId) {
    const parentGroup = nodes.find((group) => group.id === groupRefId);
    parentGroupType = parentGroup?.type;
    siblingNodes = parentGroup?.nodes || [];
  } else {
    siblingNodes = nodes;
  }

  // Get position of new question and edit statements accordingly
  let position;
  if (refId) {
    position = findIndex(siblingNodes, { id: refId }) + offset;
  } else if (node.isScreening) {
    const screeningNodes = filter(nodes, ['isScreening', true]);
    position = screeningNodes.length;
  } else {
    position = siblingNodes.length;
  }

  // the logic library expects ids to be strings and we use refIds for now
  try {
    const transformedQuestion = transformNodeToQlQuestionnaireNode({
      node,
      parentGroupType,
    });
    let newQl = questionnaireLogic.ql;

    if (isGroup(node)) {
      const { nodes: subNodes = [], ...groupOnly } = transformedQuestion;
      newQl = questionnaireLogicService.addNode(
        newQl,
        { ...groupOnly, nodes: [] },
        position,
        undefined,
        undefined,
        groupRefId,
      );
      forEach(subNodes, (subNode, index) => {
        newQl = questionnaireLogicService.addNode(
          newQl,
          subNode,
          index,
          undefined,
          undefined,
          node.refId,
        );
      });
    } else {
      newQl = questionnaireLogicService.addNode(
        newQl,
        transformedQuestion,
        position,
        undefined,
        undefined,
        groupRefId,
      );
    }

    if (node.isScreening) {
      // Added as screening: screener on, every answer allowed, no quotas; a
      // duplicated or imported question then gets its screener types back.
      newQl = qlSetScreeningStartingShape({ node, ql: newQl });
      newQl = qlSetScreenerTypes({ node, ql: newQl, screenerTypes });
    } else if (
      NODE_TYPES_SUPPORT_SIMPLE_QUOTAS.includes(node.type) &&
      node.answers.length > 0
    ) {
      newQl = transformQuotaToLogic(node, newQl);
    }

    return updateState({
      ql: newQl,
      questionnaireLogic,
    });
  } catch (error) {
    return {
      ...questionnaireLogic,
      error,
    };
  }
};

export const removeCarriedForwardAnswersFromQlByNodeId = (
  ql,
  allNodes,
  nodeId,
) => {
  let newQl = ql;

  forEach(allNodes, (currentQuestion) => {
    const carryForward = get(currentQuestion, 'carryForward', []);
    const hasCarryForwardOnNodeId = carryForward.some(
      (carriedQuestion) => carriedQuestion.questionRefId === nodeId,
    );

    if (hasCarryForwardOnNodeId) {
      const answers = get(currentQuestion, 'answers', []);
      const scorepoints = get(currentQuestion, 'scorepoints', []);
      const carriedForwardAnswers = answers.filter(
        (answer) => answer.questionRefId === nodeId,
      );
      const carriedForwardScorepoints = scorepoints.filter(
        (scorepoint) => scorepoint.questionRefId === nodeId,
      );

      forEach(carriedForwardAnswers, (answer) => {
        newQl = qlRemoveLogicOfItemAndCopies({
          ql: newQl,
          nodes: allNodes,
          nodeRefId: currentQuestion.refId,
          valueRefId: answer.refId,
        });
      });

      forEach(carriedForwardScorepoints, (scorepoint) => {
        newQl = qlRemoveLogicOfItemAndCopies({
          ql: newQl,
          nodes: allNodes,
          nodeRefId: currentQuestion.refId,
          valueRefId: scorepoint.refId,
          valueType: ActionVisibilityValueType.Column,
        });
      });
    }
  });

  return newQl;
};

const retargetScreeningAllowsAfterRemove = ({
  oldQl,
  newQl,
  removedNodeId,
}) => {
  const nodes = get(oldQl, 'questionnaire.nodes', []);
  const removedIndex = findIndex(nodes, ['id', removedNodeId]);

  // removedIndex <= 0 means the node is first (no predecessor) or lives inside a
  // group (not a top-level screening successor) — nothing to retarget.
  if (removedIndex <= 0) {
    return newQl;
  }

  const predecessor = nodes[removedIndex - 1];
  if (!predecessor || !predecessor.isScreening) {
    return newQl;
  }

  const predecessorRefId = predecessor.id;
  const oldPredStatements =
    questionnaireLogicService.findNodeStatementsByNodeIdWithNoErrorThrown(
      oldQl,
      predecessorRefId,
    );
  const newPredStatements =
    questionnaireLogicService.findNodeStatementsByNodeIdWithNoErrorThrown(
      newQl,
      predecessorRefId,
    );
  if (!oldPredStatements || !newPredStatements) {
    return newQl;
  }

  const allowsTargetingRemovedNode = questionnaireLogicService
    .getGraphStatementsWithRule(oldPredStatements)
    .filter(
      (statement) =>
        statement.outcome.type === OutcomeType.Success &&
        get(statement, 'outcome.params.id') === removedNodeId,
    );
  if (allowsTargetingRemovedNode.length === 0) {
    return newQl;
  }

  // Take the new next from questionnaire order rather than the predecessor's own
  // statements: a screening node's default is not a reliable "next" source (the
  // last screening question's default is fail-closed to FinalNode until qlUnify
  // repoints it), whereas node order always yields the correct successor.
  const { nextNodeId: nextNodeRefId = FinalNode } = Questionnaire.getNodeInfo(
    newQl.questionnaire,
    predecessorRefId,
  );

  const retargetedAllows = allowsTargetingRemovedNode.map((statement) => ({
    ...statement,
    outcome: {
      ...statement.outcome,
      params: {
        ...statement.outcome.params,
        id: nextNodeRefId,
      },
    },
  }));

  const allowIdsTargetingRemovedNode = new Set(
    allowsTargetingRemovedNode.map((statement) => statement.id),
  );
  const newStatements = new NodeStatements({
    nodeId: predecessorRefId,
    statements: [
      ...retargetedAllows,
      ...newPredStatements.statements.filter(
        (statement) => !allowIdsTargetingRemovedNode.has(statement.id),
      ),
    ],
  });

  return questionnaireLogicService.swapNodeStatements(
    newQl,
    predecessorRefId,
    newStatements,
  );
};

export const updateQlAfterNodeRemove = ({
  nodeId,
  questionnaireLogic,
  allNodes,
  shouldUpdateVersion = false,
}) => {
  try {
    const oldQl = questionnaireLogic.ql;
    let newQl = questionnaireLogicService.removeNodeWithLogic(oldQl, nodeId);

    newQl = retargetScreeningAllowsAfterRemove({
      oldQl,
      newQl,
      removedNodeId: nodeId,
    });

    newQl = removeCarriedForwardAnswersFromQlByNodeId(newQl, allNodes, nodeId);

    // this will update through the questionnairePage
    return updateState({
      ql: newQl,
      questionnaireLogic,
      shouldUpdateVersion,
    });
  } catch (error) {
    return {
      ...questionnaireLogic,
      error,
    };
  }
};

export const updateQlAfterMoveQuestion = ({
  questionnaireLogic,
  questionId,
  direction,
}) => {
  const nodeIndex = findIndex(questionnaireLogic.ql.questionnaire.nodes, [
    'id',
    questionId,
  ]);
  const newPosition = nodeIndex + direction;

  try {
    const newQl = questionnaireLogicService.moveNode(
      questionnaireLogic.ql,
      questionId,
      newPosition,
    );

    return updateState({
      ql: newQl,
      questionnaireLogic,
    });
  } catch (error) {
    return {
      ...questionnaireLogic,
      error,
    };
  }
};

const updateQlOnDrillDownNode = ({
  ql,
  node,
  checkValidConditions = false,
}) => {
  let newQl = {
    ...ql,
  };

  const { allConditions, nodeMap, statementMap } =
    questionnaireLogicService.getAllConditions(newQl);
  const drillDownConditions = allConditions.filter(
    (condition) => condition.context.questionId === node.refId,
  );
  let notValidConditions = [];

  if (checkValidConditions) {
    const isValueExist = isValueExistOnTree(
      node.matrix.validation.drillDownConfigurationDto.config.levels,
    );

    // checking every condition that is referenced on this drilldown question
    // if still exist on the config levels tree
    notValidConditions = drillDownConditions.filter(
      (condition) => !isValueExist(condition.value),
    );
  } else {
    notValidConditions = drillDownConditions;
  }

  if (notValidConditions.length > 0) {
    newQl = questionnaireLogicService.removeConditions(
      newQl,
      notValidConditions,
      nodeMap,
      statementMap,
    );
  }

  return newQl;
};

export const updateQlAfterQuestionnaireUpdate = ({
  state,
  updatedNode,
  previousNodes,
  previousNode,
  logicMode = LOGIC_MODES.PLAIN,
  updatedNodes,
}) => {
  const { targetLogic, isDisqualificationLogic } =
    getTargetLogicExtended(logicMode);
  const isPlainLogic = logicMode === LOGIC_MODES.PLAIN;

  const updatedQuestions = get(state, 'surveyDetailsExtended.questions', []);

  const isDrillDown = isDrillDownNode(updatedNode);
  const wasDrillDown = isDrillDownNode(previousNode);

  const addedAnswers = getAnswersAdded(
    previousNode.answers,
    updatedNode.answers,
    updatedNode.type,
  );

  const removedAnswers = getAnswersRemoved(
    previousNode.answers,
    updatedNode.answers,
    updatedNode.type,
  );

  let addedScorepoints = [];
  let removedScorepoints = [];

  const isMatrix = isMatrixSingleOrMultipleNode(updatedNode);
  const wasMatrix = isMatrixSingleOrMultipleNode(previousNode);

  if (isMatrix || wasMatrix) {
    addedScorepoints = getAnswersAdded(
      previousNode.scorepoints,
      updatedNode.scorepoints,
    );

    removedScorepoints = getAnswersRemoved(
      previousNode.scorepoints,
      updatedNode.scorepoints,
    );
  }

  const isMatrixOpenEnded = isMatrixOpenEndedType(updatedNode.type);
  const wasMatrixOpenEnded = isMatrixOpenEndedType(previousNode.type);

  // TODO: achil check below
  if (isMatrixOpenEnded || wasMatrixOpenEnded) {
    const previousNodeColumns = get(previousNode, 'matrix.columns', []);
    const updatedNodeColumns = get(updatedNode, 'matrix.columns', []);

    addedScorepoints = getAnswersAdded(previousNodeColumns, updatedNodeColumns);

    removedScorepoints = getAnswersRemoved(
      previousNodeColumns,
      updatedNodeColumns,
    );
  }

  let newQl;

  // ordering for these ifs is important because we deepClone and mutate references here
  try {
    if (!isEmpty(addedAnswers) && !isDrillDown) {
      // TODO: achil check below and refactor all function calls to be more DRY
      newQl = qlHandleAddedAnswers({
        addedAnswers,
        question: updatedNode,
        updatedQuestions,
        ql: state[targetLogic].ql,
      });
    }
    if ((isMatrix || isMatrixOpenEnded) && !isEmpty(addedScorepoints)) {
      newQl = qlHandleAddedScorepoints({
        addedScorepoints,
        question: updatedNode,
        updatedQuestions,
        ql: newQl || cloneDeep(state[targetLogic].ql),
      });
    }

    if (!isEmpty(removedAnswers) && !wasDrillDown) {
      newQl = qlHandleRemovedAnswers({
        removedAnswers,
        question: updatedNode,
        isDisqualificationLogic,
        previousQuestions: previousNodes,
        ql: newQl || state[targetLogic].ql,
        updatedQuestions,
      });
    }
    if (isMatrix || wasMatrix || isMatrixOpenEnded || wasMatrixOpenEnded) {
      newQl = qlHandleRemovedScorepoints({
        question: updatedNode,
        removedScorepoints,
        isDisqualificationLogic,
        previousQuestions: previousNodes,
        ql: newQl || state[targetLogic].ql,
        updatedQuestions,
      });
    }

    if (isDrillDown) {
      const hadAnswers = !isEmpty(previousNode.answers);
      const hasAnswers = !isEmpty(updatedNode.answers);

      // during delete of drilldown options
      if (hadAnswers && !hasAnswers && wasDrillDown) {
        newQl = updateQlOnDrillDownNode({
          ql: newQl || state[targetLogic].ql,
          node: updatedNode,
        });
      } else if (hasAnswers && removedAnswers.length > 0) {
        newQl = updateQlOnDrillDownNode({
          ql: newQl || state[targetLogic].ql,
          node: updatedNode,
          checkValidConditions: true,
        });
      }
    }
  } catch (error) {
    console.error('Error updating QL after question update', error);
    return {
      ...state[targetLogic],
      error,
    };
  }
  // Remove any leftover statements in case something has gone wrong.
  // If we have a question that has no answers but has statements that reference answers
  // that do not exist, this code will remove them.

  const hasChangedType = updatedNode.type !== previousNode.type;
  if (hasChangedType && !shouldKeepLogic(updatedNode.type, previousNode.type)) {
    newQl = newQl || state[targetLogic].ql;
    const { refId } = updatedNode;
    const { nextNodeId } = Questionnaire.getNodeInfo(
      newQl.questionnaire,
      refId,
    );
    const newQlNode = transformNodesForUseWithQuestionnaireLogic([updatedNode]);

    newQl = questionnaireLogicService.reInitializeNode({
      ql: newQl,
      id: refId,
      nextNode: nextNodeId,
      node: first(newQlNode),
      updateQuestionnaireNodes:
        targetLogic !== LOGIC_MODE_KEYS.DISQUALIFICATION,
    });
  } else if (hasChangedType) {
    newQl = cloneDeep(newQl || state[targetLogic].ql);
    const { refId, type: newType } = updatedNode;
    const { node: logicNode } = Questionnaire.getNodeInfo(
      newQl.questionnaire,
      refId,
    );
    if (logicNode) {
      logicNode.type = newType;
    }
  }

  // add quota to newly added answers/scalepoints if required (screening nodes
  // do it after their screening step, once new answers are allowed)
  try {
    if (isPlainLogic && !updatedNode.isScreening) {
      newQl = handleMissingQuotasForNode(
        {
          ...state,
          [targetLogic]: {
            ...state[targetLogic],
            ql: newQl || state[targetLogic].ql,
          },
        },
        updatedNode,
        targetLogic,
      );
    }
  } catch (error) {
    console.error('Error adding missing quotas for node', error);
    throw error;
  }

  newQl = qlHandleLoopAndMergeUpdates({
    ql: newQl || state[targetLogic].ql,
    removedAnswers,
    removedScorepoints,
    nodes: previousNodes,
    sourceNode: updatedNode,
  });
  // Update L&M groups affected by cascading changes (currently only for repetitions)
  newQl = qlHandleLoopAndMergeRepetitions({
    ql: newQl || state[targetLogic].ql,
    allNodes: updatedNodes,
    sourceNode: updatedNode,
  });

  if (isPlainLogic && updatedNode.isScreening) {
    const workingQl = newQl || state[targetLogic].ql;
    // A type change that can't keep the logic reinitialized the node above.
    newQl =
      hasChangedType && !shouldKeepLogic(updatedNode.type, previousNode.type)
        ? qlSetScreeningStartingShape({ node: updatedNode, ql: workingQl })
        : qlUpdateScreeningNode({
            node: updatedNode,
            previousNode,
            ql: workingQl,
            previousQl: state[targetLogic].ql,
          });
    newQl = qlSyncScreeningQuotas({
      state,
      targetLogic,
      node: updatedNode,
      previousNode,
      ql: newQl,
      previousQl: state[targetLogic].ql,
    });
  }

  if (newQl) {
    // this will update through the questionnairePage
    const newState = updateState({
      ql: newQl,
      questionnaireLogic: state[targetLogic],
      shouldUpdateVersion: false,
    });
    return newState;
  }

  return state[targetLogic];
};

/**
 * Keeps a screening node's quota rules in step with its screener types after
 * an edit (`previousNode` and `previousQl` are the node and the rules before
 * it): a quota goes with an item leaving allowed, only items that became
 * eligible get a new quota (at 0), and the statements are put back in order.
 */
export function qlSyncScreeningQuotas({
  state,
  targetLogic = LOGIC_MODE_KEYS.PLAIN,
  node,
  previousNode = node,
  ql,
  previousQl,
}) {
  let newQl = qlUpdateScreeningQuotas({ node, ql });
  newQl = handleMissingQuotasForNode(
    { ...state, [targetLogic]: { ...state[targetLogic], ql: newQl } },
    node,
    targetLogic,
    {
      isEligibleItem: makeNewlyEligibleScreeningQuotaItems({
        node,
        previousNode,
        ql: newQl,
        previousQl,
        unconditionallyHiddenItems: getUnconditionallyHiddenItems(
          state,
          targetLogic,
        ),
      }),
    },
  );
  return qlOrderScreeningStatements({ ql: newQl, nodeId: node.refId });
}

export const qlHandleLoopAndMergeUpdates = ({
  ql,
  removedAnswers = [],
  removedScorepoints = [],
  nodes = [],
  sourceNode,
}) => {
  // filter nodes for the loop and merge ones that are based on the updatedNode
  const relevantLoopAndMergeNodes = nodes.filter(
    (loopAndMergeNode) =>
      isLoopAndMergeGroup(loopAndMergeNode.type) &&
      get(
        loopAndMergeNode,
        'loopAndMergeFromQuestionConfiguration.questionRefId',
      ) === sourceNode.refId,
  );
  if (relevantLoopAndMergeNodes.lengh === 0) return ql;
  if (removedAnswers.length === 0 && removedScorepoints.length === 0) return ql;

  let updatedQl = ql;
  const removedAnswersAndScorepoints =
    removedAnswers.concat(removedScorepoints);

  removedAnswersAndScorepoints.forEach((removedAnswerOrScorepoint) => {
    relevantLoopAndMergeNodes.forEach((loopAndMergeNode) => {
      const removedConcept = getConceptBySourceId(
        loopAndMergeNode,
        removedAnswerOrScorepoint.refId,
      );
      if (removedConcept) {
        updatedQl = questionnaireLogicService.removeConditionsWithValue(
          updatedQl,
          removedConcept.refId,
          QuestionnaireConditionContext.Concept,
        );
      }
    });
  });

  return updatedQl;
};

export const updateQlAfterScreeningUpdate = ({
  state,
  screeningQuestion,
  previousScreeningQuestion,
  previousQuestions,
  targetLogic = 'questionnaireLogic',
}) => {
  const updatedQuestions = get(state, 'surveyDetailsExtended.questions', []);
  const isDisqualificationLogic =
    targetLogic === LOGIC_MODE_KEYS.DISQUALIFICATION;

  const addedAnswers = getAnswersAdded(
    previousScreeningQuestion?.answers,
    screeningQuestion?.answers,
  );

  const removedAnswers = getAnswersRemoved(
    previousScreeningQuestion?.answers,
    screeningQuestion?.answers,
  );

  let newQl;
  // ordering for these ifs is important because we deepClone and mutate references here
  try {
    if (!isEmpty(addedAnswers)) {
      newQl = qlHandleAddedAnswers({
        addedAnswers,
        question: screeningQuestion,
        updatedQuestions,
        ql: state[targetLogic].ql,
      });
    }

    if (!isEmpty(removedAnswers)) {
      newQl = qlHandleRemovedAnswers({
        removedAnswers,
        question: screeningQuestion,
        isDisqualificationLogic,
        previousQuestions,
        ql: newQl || state[targetLogic].ql,
      });
    }
  } catch (error) {
    return {
      ...state[targetLogic],
      error,
    };
  }

  if (newQl) {
    // this will update through the questionnairePage
    return updateState({
      ql: newQl,
      questionnaireLogic: state[targetLogic],
      shouldUpdateVersion: false,
    });
  }

  return state[targetLogic];
};

export function getQlSubNodes(
  node,
  withNextNode = false,
  defaultNextNode = FinalNode,
) {
  let nodes;

  if (node.questions) {
    nodes = node.questions.map((subNode) =>
      transformNodeToQlQuestionnaireNode({
        node: subNode,
        sameLevelNodes: node.questions,
        withNextNode,
        defaultNextNode,
        parentGroupType: node.type,
      }),
    );
  }

  return nodes;
}

function qlHandleAddedAnswers({
  addedAnswers,
  question,
  ql,
  updatedQuestions,
}) {
  if (!QUESTIONS_SUPPORTING_LOGIC_ANSWERS.includes(question.type)) {
    // eslint-disable-next-line
    console.warn('Adding answers on ql question a non supported question type');
  }
  let newQl = cloneDeep(ql);

  const newQuestionnaire = get(newQl, 'questionnaire', {});
  const { node: logicQuestion } = Questionnaire.getNodeInfo(
    newQuestionnaire,
    question.refId,
  );

  if (logicQuestion) {
    logicQuestion.answers = question.answers.map((answer) => ({
      id: answer.refId,
    }));
  }

  const questionsWithCarriedAnswers = updatedQuestions.reduce(
    (acc, currentQuestion) => {
      const foundQuestion = find(currentQuestion.carryForward, [
        'questionRefId',
        question.refId,
      ]);
      if (foundQuestion) {
        return [
          ...acc,
          {
            answerAs: foundQuestion.answerAs,
            question: currentQuestion,
          },
        ];
      }
      return acc;
    },
    [],
  );

  forEach(questionsWithCarriedAnswers, ({ question: cfQuestion, answerAs }) => {
    const { node: logicQuestionToUpdate } = Questionnaire.getNodeInfo(
      newQuestionnaire,
      cfQuestion.refId,
    );

    if (logicQuestionToUpdate) {
      const newAnswers = addedAnswers.reduce((acc, currentAnswer) => {
        const addedItemInCFquestion = find(cfQuestion[answerAs], [
          'originalAnswerRefId',
          currentAnswer.refId,
        ]);

        if (
          addedItemInCFquestion &&
          findIndex(logicQuestionToUpdate[answerAs], [
            'id',
            addedItemInCFquestion.refId,
          ]) === -1
        ) {
          return [...acc, { id: addedItemInCFquestion.refId }];
        }
        return acc;
      }, []);

      logicQuestionToUpdate[answerAs] = [
        ...logicQuestionToUpdate[answerAs],
        ...newAnswers,
      ];
    }
  });

  newQl = qlHandleLoopAndMergeRepetitions({
    ql: newQl,
    allNodes: updatedQuestions,
    sourceNode: question,
  });

  return newQl;
}

function qlHandleAddedScorepoints({
  ql,
  addedScorepoints,
  question,
  updatedQuestions,
}) {
  let newQl = ql;
  const newQuestionnaire = get(newQl, 'questionnaire', {});
  const { node: logicQuestion } = Questionnaire.getNodeInfo(
    newQuestionnaire,
    question.refId,
  );

  if (logicQuestion) {
    // handle new matrix schema
    const isMatrixOpenEnded = isNewMatrixNode(question);

    if (isMatrixOpenEnded) {
      const matrixColumns = get(question, 'matrix.columns', []);
      logicQuestion.scorepoints = matrixColumns.map((scorepoint) => ({
        id: scorepoint.refId,
      }));
    } else {
      logicQuestion.scorepoints = question.scorepoints.map((scorepoint) => ({
        id: scorepoint.refId,
      }));
    }
    const questionsWithCarriedAnswers = updatedQuestions.reduce(
      (acc, currentQuestion) => {
        const foundQuestion = find(currentQuestion.carryForward, [
          'questionRefId',
          question.refId,
        ]);
        if (foundQuestion) {
          return [...acc, currentQuestion];
        }
        return acc;
      },
      [],
    );

    // TODO: [GABE] handle "scorepoints" from open ended matrix
    // and the new format we use to store the CF relation inside the column
    forEach(questionsWithCarriedAnswers, (cfQuestion) => {
      const { node: questionToUpdate } = Questionnaire.getNodeInfo(
        newQuestionnaire,
        cfQuestion.refId,
      );

      if (questionToUpdate) {
        const newAnswers = addedScorepoints.reduce((acc, currentScorepoint) => {
          const addedItemInCFquestion = find(cfQuestion.answers, [
            'originalScorepointRefId',
            currentScorepoint.refId,
          ]);
          if (
            addedItemInCFquestion &&
            findIndex(questionToUpdate.answers, [
              'id',
              addedItemInCFquestion.refId,
            ]) === -1
          ) {
            return [...acc, { id: addedItemInCFquestion.refId }];
          }
          return acc;
        }, []);

        questionToUpdate.answers = [...questionToUpdate.answers, ...newAnswers];
      }
    });

    newQl = qlHandleLoopAndMergeRepetitions({
      ql: newQl,
      allNodes: updatedQuestions,
      sourceNode: question,
    });
  }
  return newQl;
}

function qlHandleRemovedAnswers({
  ql,
  removedAnswers,
  question,
  isDisqualificationLogic,
  previousQuestions = [],
  updatedQuestions,
}) {
  let newQl = ql;
  let updateQuestionnaireNode;
  try {
    const { node: qlNode } = Questionnaire.getNodeInfo(
      ql.questionnaire,
      question.refId,
    );
    updateQuestionnaireNode = Boolean(qlNode);
  } catch (e) {
    updateQuestionnaireNode = false;
  }

  const logicNodeRefId = isDisqualificationLogic
    ? DISQUALIFY_PLACEHOLDER_QUESTION.id
    : undefined;

  forEach(removedAnswers, (answer) => {
    // Only update ql.questionnaire when the source node is present there
    // (unified/Peeq). Classic screening paths keep the node out of that graph.
    newQl = questionnaireLogicService.removeAnswerWithLogic(
      newQl,
      isDisqualificationLogic
        ? DISQUALIFY_PLACEHOLDER_QUESTION.id
        : question.refId,
      answer.refId,
      null,
      updateQuestionnaireNode,
    );

    // if is carried forward, remove those answers as well
    forEach(previousQuestions, (currentQuestion) => {
      if (QUESTION_TYPES_WITH_NO_ANSWER.includes(currentQuestion.type)) {
        if (answer.questionRefId === currentQuestion.refId) {
          newQl = questionnaireLogicService.removeAnswerWithLogic(
            newQl,
            isDisqualificationLogic
              ? DISQUALIFY_PLACEHOLDER_QUESTION.id
              : question.refId,
            answer.refId,
          );
        }
      } else {
        forEach(removedAnswers, (answerToCheck) => {
          const matchedAnswer = find(currentQuestion.answers, [
            'originalAnswerRefId',
            answerToCheck.refId,
          ]);
          const matchedScorepoint = find(currentQuestion.scorepoints, [
            'originalAnswerRefId',
            answerToCheck.refId,
          ]);
          if (matchedAnswer) {
            newQl = qlRemoveLogicOfItemAndCopies({
              ql: newQl,
              nodes: previousQuestions,
              nodeRefId: currentQuestion.refId,
              valueRefId: matchedAnswer.refId,
              logicNodeRefId,
            });
          }
          if (matchedScorepoint) {
            newQl = qlRemoveLogicOfItemAndCopies({
              ql: newQl,
              nodes: previousQuestions,
              nodeRefId: currentQuestion.refId,
              valueRefId: matchedScorepoint.refId,
              valueType: ActionVisibilityValueType.Column,
              logicNodeRefId,
            });
          }
        });
      }
    });
  });

  newQl = qlHandleLoopAndMergeRepetitions({
    ql: newQl,
    allNodes: updatedQuestions,
    sourceNode: question,
  });

  return newQl;
}

function qlHandleRemovedScorepoints({
  ql,
  question,
  removedScorepoints,
  isDisqualificationLogic,
  previousQuestions,
  updatedQuestions,
}) {
  let newQl = ql;
  const logicNodeRefId = isDisqualificationLogic
    ? DISQUALIFY_PLACEHOLDER_QUESTION.id
    : undefined;

  forEach(removedScorepoints, (scorepoint) => {
    newQl = questionnaireLogicService.removeAnswerWithLogic(
      newQl,
      isDisqualificationLogic
        ? DISQUALIFY_PLACEHOLDER_QUESTION.id
        : question.refId,
      null,
      scorepoint.refId,
    );

    forEach(previousQuestions, (currentQuestion) => {
      forEach(removedScorepoints, (scorepointToCheck) => {
        const matchedAnswer = find(currentQuestion.answers, [
          'originalScorepointRefId',
          scorepointToCheck.refId,
        ]);
        if (matchedAnswer) {
          newQl = qlRemoveLogicOfItemAndCopies({
            ql: newQl,
            nodes: previousQuestions,
            nodeRefId: currentQuestion.refId,
            valueRefId: matchedAnswer.refId,
            logicNodeRefId,
          });
        }

        const matchedScorepoint = find(currentQuestion.scorepoints, [
          'originalScorepointRefId',
          scorepointToCheck.refId,
        ]);
        if (matchedScorepoint) {
          newQl = qlRemoveLogicOfItemAndCopies({
            ql: newQl,
            nodes: previousQuestions,
            nodeRefId: currentQuestion.refId,
            valueRefId: matchedScorepoint.refId,
            valueType: ActionVisibilityValueType.Column,
            logicNodeRefId,
          });
        }
      });
    });
  });

  newQl = qlHandleLoopAndMergeRepetitions({
    ql: newQl,
    allNodes: updatedQuestions,
    sourceNode: question,
  });

  return newQl;
}

function qlHandleLoopAndMergeRepetitions({
  ql,
  allNodes = [],
  sourceNode = {},
}) {
  const loopAndMergeGroups = allNodes.filter((lmQuestion) =>
    isLoopAndMergeGroup(lmQuestion.type),
  );
  const loopAndMergeGroupsWithCarriedValues = loopAndMergeGroups.filter(
    (lmQuestion) =>
      lmQuestion.loopAndMergeFromQuestionConfiguration?.questionRefId ===
      sourceNode.refId,
  );
  if (loopAndMergeGroupsWithCarriedValues.length === 0) {
    return ql;
  }

  const newQl = cloneDeep(ql);
  const newQuestionnaire = get(newQl, 'questionnaire', {});
  forEach(loopAndMergeGroupsWithCarriedValues, (lmQuestion) => {
    const { node: lmLogicQuestion } = Questionnaire.getNodeInfo(
      newQuestionnaire,
      lmQuestion.refId,
    );
    if (lmLogicQuestion) {
      lmLogicQuestion.repetitions = lmQuestion?.properties?.repetitions;
    }
  });
  return newQl;
}

function transformQuotaToLogicStatements(payload) {
  const { nodeRefId, answers = [] } = payload;
  const statements = [];

  answers.forEach((answer) => {
    if (!isFinite(answer.quota)) {
      return;
    }

    const condition = getAnswerCondition({
      questionRefId: nodeRefId,
      defaultValue: answer.refId,
      matcher: Matcher.Equal,
    });
    const rule = new Rule({
      operator: Operator.Any,
      conditions: [condition],
      contextType: QuestionnaireConditionContext.Answer,
    });
    rule.quota = conditionService.createCondition(
      answer.quota,
      new QuotaConditionContext(rule.id, QuotaConditionTypeEnum.Simple),
      getDefaultMatcherForQuota(),
    );

    statements.push(
      new Statement({
        rule,
        outcome: new Outcome({
          type: OutcomeType.Failure,
          reason: REASON_QUESTIONNAIRE_QUOTA_FULL,
          params: new NextNode({
            id: FinalNode,
          }),
        }),
      }),
    );
  });

  return statements;
}

function transformMatrixQuotaToLogicStatements(payload) {
  const {
    nodeRefId,
    answers = [],
    // scorepoints = [],
    nextNodeRefId,
  } = payload;
  const statements = [];

  answers.forEach((answer) => {
    const { quota } = answer;
    const hasQuota = quota && quota.length;
    if (!hasQuota) {
      return;
    }

    quota.forEach((rowColumnQuota) => {
      const outcomeType = OutcomeType.Failure;
      let nextNodeId = END_OF_SURVEY;
      let reason = REASON_SCREENOUT;

      nextNodeId = nextNodeRefId;
      reason = '';

      const condition = getAnswerCondition({
        questionRefId: nodeRefId,
        defaultValue: {
          row: answer.refId,
          column: rowColumnQuota.columnRefId,
        },
        matcher: Matcher.Equal,
      });

      const rule = new Rule({
        operator: Operator.Any,
        conditions: [condition],
        contextType: QuestionnaireConditionContext.Answer,
      });

      if (rowColumnQuota) {
        rule.quota = conditionService.createCondition(
          rowColumnQuota.completes,
          new QuotaConditionContext(rule.id, QuotaConditionTypeEnum.Simple),
          getDefaultMatcherForQuota(),
        );
        reason = REASON_QUESTIONNAIRE_QUOTA_FULL;
      }

      statements.push(
        new Statement({
          rule,
          outcome: new Outcome({
            type: outcomeType,
            reason,
            params: new NextNode({
              id: nextNodeId,
            }),
          }),
        }),
      );
    });
  });

  return statements;
}

// Rebuilds a survey-body question's answer quota statements from its answers'
// quotas. Screening questions keep their rules in the store (`screening/`).
export function transformQuotaToLogic(question, ql) {
  const { type, answers = [], refId: nodeRefId } = question;
  let statements = [];

  const qStatements = questionnaireLogicService.findNodeStatementsByNodeId(
    ql,
    nodeRefId,
  );
  const defaultStatement = qStatements.statements.find(({ rule }) => !rule);
  const nextNodeRefId = get(defaultStatement, 'outcome.params.id', FinalNode);
  const payload = { nodeRefId, answers, nextNodeRefId };
  if (isEmpty(answers)) {
    statements = [];
  } else if (isSingleQuestionType(type) || isMultipleQuestionType(type)) {
    statements = transformQuotaToLogicStatements(payload);
  } else if (isMatrixSingleMultipleOrBipolarQuestionType(type)) {
    statements = transformMatrixQuotaToLogicStatements(payload);
  }

  // remove simple quota statements if exist
  const nonSimpleQuotaStatements = qStatements.statements.filter(({ rule }) => {
    if (!rule) return false;

    if (rule && !rule.quota) {
      return true;
    }
    return rule.quota.context.type !== QuotaConditionTypeEnum.Simple;
  });

  return questionnaireLogicService.swapNodeStatements(
    ql,
    nodeRefId,
    new NodeStatements({
      nodeId: nodeRefId,
      statements: [
        ...nonSimpleQuotaStatements,
        ...statements,
        defaultStatement,
      ],
    }),
  );
}

function buildQuestionsByRefIdMap(questions) {
  const questionsByRefId = new Map();

  if (!questions?.length) {
    return questionsByRefId;
  }

  const stack = [...questions];
  while (stack.length) {
    const question = stack.pop();
    if (question?.refId) {
      questionsByRefId.set(question.refId, question);
    }
    if (question?.questions?.length) {
      stack.push(...question.questions);
    }
  }

  return questionsByRefId;
}

export function getRuleEntries(ql, quotaMinimalMap, questions) {
  const entries = questionnaireLogicService.getQuotaRuleEntries(ql);

  if (entries) {
    const questionsByRefId = buildQuestionsByRefIdMap(questions);

    return entries.map((entry, order) => {
      const ruleEntry = {
        ...entry,
        order,
      };

      if (ruleEntry.ruleType === QuotaConditionTypeEnum.Simple) {
        const quotaMapEntry = find(
          get(quotaMinimalMap, [entry.questionRefId, 'items'], []),
          { ruleId: entry.ruleId },
        );
        const question = questionsByRefId.get(entry.questionRefId);

        if (
          quotaMapEntry &&
          getQuestionSupportsQuotaAssignment(question?.type) &&
          isSingleAssignmentStrategy(question?.quotaAssignmentStrategy)
        ) {
          ruleEntry.previousRevisionsAssigned =
            quotaMapEntry.previousRevisionsAssigned || 0;
        }
      }

      return ruleEntry;
    });
  }
  return entries;
}

export function getNonEmptyQl(ql) {
  if (!ql) {
    return undefined;
  }

  if (ql.questionnaire.nodes.length === 0) {
    return undefined;
  }

  return ql;
}

export function updateQlQuestionnaire({ nodes = [], ql = {} }) {
  const newQuestionnaire = nodes.map((node) =>
    transformNodeToQlQuestionnaireNode({ node, sameLevelNodes: nodes }),
  );

  return {
    ...ql,
    questionnaire: { ...ql.questionnaire, nodes: newQuestionnaire },
  };
}

export function transformNodeToQlQuestionnaireNode({
  node,
  sameLevelNodes = [],
  withNextNode = false,
  defaultNextNode = FinalNode,
  parentGroupType,
}) {
  const nextNodeIdObjectToSpread = {};
  if (withNextNode) {
    const currentIndex = findIndex(sameLevelNodes, { refId: node.refId });
    const nextNodeId = get(
      sameLevelNodes,
      `[${currentIndex + 1}].refId`,
      defaultNextNode,
    );
    nextNodeIdObjectToSpread.nextQuestion = nextNodeId;
  }
  const groupPolicyDoesNotAllowAnswersAndScorepointsInQl =
    isConjointGroup(parentGroupType) || isMaxDiffGroup(parentGroupType);

  switch (node.type) {
    // handle the groups
    case GROUP_BASIC:
    case GROUP_LOOP_MERGE:
    case GROUP_CONJOINT:
    case GROUP_MAXDIFF:
    case GROUP_VAN_WESTENDORP:
    case GROUP_AB_TEST: {
      const nodeQl = {
        answers: [],
        id: node.refId,
        type: node.type,
        nodes: getQlSubNodes(
          node,
          withNextNode,
          nextNodeIdObjectToSpread.nextQuestion,
        ),
      };
      if (has(node, 'properties.repetitions')) {
        nodeQl.repetitions = node.properties.repetitions;
      }
      return nodeQl;
    }
    // handle question with "normal" scorepoints
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE:
      return Object.assign(
        {
          id: node.refId,
          answers: groupPolicyDoesNotAllowAnswersAndScorepointsInQl
            ? []
            : node.answers?.map((answer) => ({
                id: answer.refId,
                ...nextNodeIdObjectToSpread,
              })),
          scorepoints: groupPolicyDoesNotAllowAnswersAndScorepointsInQl
            ? []
            : node.scorepoints?.map((scorepoint) => ({
                id: scorepoint.refId,
              })),
          type: node.type,
        },
        node.isScreening ? { isScreening: node.isScreening } : undefined,
      );
    // handle question with new matrix scorepoints
    case QUESTION_MATRIX_CONSTANT_SUM:
    case QUESTION_MATRIX_NUMERIC_OPEN_ENDED:
    case QUESTION_MATRIX_OPEN_ENDED:
      return Object.assign(
        {
          id: node.refId,
          answers: groupPolicyDoesNotAllowAnswersAndScorepointsInQl
            ? []
            : node.answers?.map((answer) => ({
                id: answer.refId,
                ...nextNodeIdObjectToSpread,
              })),
          scorepoints: groupPolicyDoesNotAllowAnswersAndScorepointsInQl
            ? []
            : node.matrix?.columns?.map((column) => ({
                id: column.refId,
              })),
          type: node.type,
        },
        node.isScreening ? { isScreening: node.isScreening } : undefined,
      );
    // handle questions where we do not store answers in the ql.questionnaire
    case QUESTION_MAXDIFF:
    case QUESTION_DRILLDOWN:
    case QUESTION_DESCRIPTION:
    case QUESTION_OPEN_ENDED:
    case QUESTION_NUMERIC_OPEN_ENDED:
    case QUESTION_NPS:
    case QUESTION_SLIDER:
    case QUESTION_RATING:
      return Object.assign(
        {
          answers: [],
          id: node.refId,
          type: node.type,
        },
        node.isScreening ? { isScreening: node.isScreening } : undefined,
      );
    // handle simple questions with just answers
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
    case QUESTION_RANKING:
    case QUESTION_MATRIX_BIPOLAR:
    case QUESTION_CONVERSATIONAL:
    default:
      return Object.assign(
        {
          id: node.refId,
          answers: groupPolicyDoesNotAllowAnswersAndScorepointsInQl
            ? []
            : node.answers?.map((answer) => ({
                id: answer.refId,
                ...nextNodeIdObjectToSpread,
              })),
          type: node.type,
        },
        node.isScreening ? { isScreening: node.isScreening } : undefined,
      );
  }
}

export function getLogicKeyFromMode(logicMode) {
  switch (logicMode) {
    case LOGIC_MODES.PLAIN:
      return LOGIC_MODE_KEYS.PLAIN;
    case LOGIC_MODES.DISQUALIFICATION:
      return LOGIC_MODE_KEYS.DISQUALIFICATION;
    case LOGIC_MODES.HIDDEN_VARIABLES:
      return LOGIC_MODE_KEYS.PLAIN;
    case LOGIC_MODES.ADVANCED_QUOTA_LOGIC:
      return LOGIC_MODE_KEYS.ADVANCED_QUOTA_LOGIC;
    case LOGIC_MODES.DISPLAY_LOGIC:
      return LOGIC_MODE_KEYS.DISPLAY_LOGIC;
    default:
      return LOGIC_MODE_KEYS.PLAIN;
  }
}

export function getTargetLogic(logicMode) {
  return getLogicKeyFromMode(logicMode);
}

export function getTargetLogicExtended(logicMode) {
  const targetLogic = getTargetLogic(logicMode);

  return {
    targetLogic,
    isDisqualificationLogic: logicMode === LOGIC_MODES.DISQUALIFICATION,
    isHiddenVariablesLogic: logicMode === LOGIC_MODES.HIDDEN_VARIABLES,
    isDisplayLogic: logicMode === LOGIC_MODES.DISPLAY_LOGIC,
    isAdvancedQuotaLogic: logicMode === LOGIC_MODES.ADVANCED_QUOTA_LOGIC,
  };
}

export function findNodeStatementsByNodeId(ql, nodeId) {
  if (!ql?.nodeStatements) {
    return undefined;
  }

  return ql.nodeStatements.find(
    (nodeStatement) => nodeStatement.nodeId === nodeId,
  );
}

export function filterStatementsByNodeIdByOutcomeTypes(
  ql,
  nodeId,
  outcomeTypes,
) {
  const nodeStatements = findNodeStatementsByNodeId(ql, nodeId);

  if (!nodeStatements) {
    return [];
  }

  return questionnaireLogicService.filterStatementsByOutcomeType(
    nodeStatements.statements,
    outcomeTypes,
  );
}

export function findStatementIdByOutcomeActionValue({
  ql,
  nodeId,
  valueId,
  valueType,
}) {
  const outcomeType =
    valueType === ActionVisibilityValueType.Node
      ? OutcomeType.Node
      : OutcomeType.Answer;
  const statements = filterStatementsByNodeIdByOutcomeTypes(ql, nodeId, [
    outcomeType,
  ]);
  const predicateFn =
    outcomeType === OutcomeType.Node
      ? (action) => action.targetId === nodeId
      : (action) => action.value && action.value.includes(valueId);

  return statements.find((statement) =>
    statement.outcome.actions.some(predicateFn),
  )?.id;
}

export const getHiddenVariablesForNode = ({
  nodeRefId,
  variableActionsPerNode = {},
  hiddenVariables,
}) => {
  const variables = [];
  const nodeVariables = variableActionsPerNode[nodeRefId];
  if (!nodeVariables) {
    return { hiddenVariables: [] };
  }
  nodeVariables.forEach(([action, refId]) => {
    const foundVariable = find(hiddenVariables, ['refId', refId]);
    variables.push({
      refId,
      action,
      label: foundVariable?.label,
    });
  });
  return { hiddenVariables: variables };
};

export function isGroupNodeRefIdWithPostFix(nodeRefId) {
  return nodeRefId.includes(VirtualEntryGroupPostfix);
}

export function getNodeRefIdWithoutPostFix(nodeRefId) {
  if (isGroupNodeRefIdWithPostFix(nodeRefId)) {
    return nodeRefId.replace(VirtualEntryGroupPostfix, '');
  }

  return nodeRefId;
}

const filterStatementsByQuotaReason = (statements, keepQuotas) =>
  statements.filter((statement) => {
    const outcome = get(statement, 'outcome', {});
    const { reason } = outcome;
    return isQuotaFullReason(reason) === keepQuotas;
  });

// filter quota statements by type (simple or advanced)
// if type is null, it implies screening question quotas (which do not have type set)
export const getFilteredQuotaStatementsByType = (
  statements = [],
  type = null,
) => {
  const quotaStatements = filterStatementsByQuotaReason(statements, true);
  const advancedQuota = quotaStatements.filter((statement) => {
    const statementType = get(statement, 'rule.quota.context.type', null);
    return statementType === type;
  });

  return advancedQuota;
};

export const getNonQuotaStatements = (statements) =>
  filterStatementsByQuotaReason(statements, false);

export function generateQuotaRuleForAnswer({
  questionRefId,
  answerRefId,
  quotaValue,
  ql,
  isScreening = false,
}) {
  const { statements } = questionnaireLogicService.findNodeStatementsByNodeId(
    ql,
    questionRefId,
  );

  // if there is already a simple quota rule for this answer, we skip adding another one
  if (
    statements.some(({ rule }) => {
      if (!rule || !rule.quota) {
        return false;
      }
      const condition = rule.conditions[0];
      const isSimpleQuota =
        rule.quota?.context?.type === QuotaConditionTypeEnum.Simple;
      const isForSameAnswer = condition.value === answerRefId;
      return isSimpleQuota && isForSameAnswer;
    })
  ) {
    return ql;
  }

  const condition = getAnswerCondition({
    questionRefId,
    defaultValue: answerRefId,
    matcher: Matcher.Equal,
  });
  const rule = new Rule({
    operator: Operator.Any,
    conditions: [condition],
    contextType: QuestionnaireConditionContext.Answer,
  });
  rule.quota = conditionService.createCondition(
    quotaValue,
    new QuotaConditionContext(rule.id, QuotaConditionTypeEnum.Simple),
    getDefaultMatcherForQuota(),
  );
  const statement = new Statement({
    rule,
    outcome: new Outcome({
      type: OutcomeType.Failure,
      reason: isScreening
        ? REASON_SCREENING_QUOTA_FULL
        : REASON_QUESTIONNAIRE_QUOTA_FULL,
      params: new NextNode({
        id: FinalNode,
      }),
    }),
  });

  const newStatements = new NodeStatements({
    nodeId: questionRefId,
    statements: statements.concat([statement]),
  });

  const newQl = questionnaireLogicService.swapNodeStatements(
    ql,
    questionRefId,
    newStatements,
  );
  // On a screening node the quota goes before the navigation statements.
  return isScreening
    ? qlOrderScreeningStatements({ ql: newQl, nodeId: questionRefId })
    : newQl;
}

export function filterVisibleNodeStatements(statements, node) {
  if (!node?.isScreening) {
    return statements;
  }

  return statements.filter((statement) => !isScreeningStatement(statement));
}

export function generateQuotaRuleForAnswerColumn({
  questionRefId,
  answerRefId,
  columnRefId,
  quotaValue,
  ql,
}) {
  const { statements } = questionnaireLogicService.findNodeStatementsByNodeId(
    ql,
    questionRefId,
  );

  // if there is already a simple quota rule for this answer column pair, we skip adding another one
  if (
    statements.some(({ rule }) => {
      if (!rule || !rule.quota) {
        return false;
      }
      const condition = rule.conditions[0];
      const isSimpleQuota =
        rule.quota?.context?.type === QuotaConditionTypeEnum.Simple;
      const isForSameAnswerColumn =
        condition.value.row === answerRefId &&
        condition.value.column === columnRefId;
      return isSimpleQuota && isForSameAnswerColumn;
    })
  ) {
    return ql;
  }

  const condition = getAnswerCondition({
    questionRefId,
    defaultValue: {
      row: answerRefId,
      column: columnRefId,
    },
    matcher: Matcher.Equal,
  });

  const rule = new Rule({
    operator: Operator.Any,
    conditions: [condition],
    contextType: QuestionnaireConditionContext.Answer,
  });
  rule.quota = conditionService.createCondition(
    quotaValue,
    new QuotaConditionContext(rule.id, QuotaConditionTypeEnum.Simple),
    getDefaultMatcherForQuota(),
  );
  const statement = new Statement({
    rule,
    outcome: new Outcome({
      type: OutcomeType.Failure,
      reason: REASON_QUESTIONNAIRE_QUOTA_FULL,
      params: new NextNode({
        id: FinalNode,
      }),
    }),
  });

  const newStatements = new NodeStatements({
    nodeId: questionRefId,
    statements: statements.concat([statement]),
  });

  return questionnaireLogicService.swapNodeStatements(
    ql,
    questionRefId,
    newStatements,
  );
}
